import { Trophy, Award, Star } from 'lucide-react';
import { allAchievements, getEarnedAchievements } from '../../../services/achievementService';
import { useTranslation } from 'react-i18next';

export const AchievementsDetailPanel = ({ achievementStats, userProgress, onNavigate }) => {
  const { t } = useTranslation();

  // Get REAL achievements using your existing service
  const earnedAchievements = getEarnedAchievements(userProgress);
  const allAchievementsList = allAchievements || [];

  const unlockedCount = earnedAchievements?.length || achievementStats?.achievementsUnlocked || 0;
  const totalPoints = achievementStats?.totalPoints || 0;

  // Map achievements to show unlocked status
  const achievementsWithStatus = allAchievementsList.map(ach => ({
    ...ach,
    unlocked: earnedAchievements?.some(earned => earned.id === ach.id) || false
  }));

  return (
    <>
      {/* Summary Card */}
      <div
        className="group relative bg-gradient-to-br from-[#3A86FF] to-blue-700 text-white rounded-2xl p-6 overflow-hidden border-[3px] border-[#3A86FF]"
        style={{
          animation: 'slideInScale 0.5s ease-out',
          boxShadow: '0 20px 60px rgba(58, 134, 255, 0.3)'
        }}
      >
        {/* Decorative circles */}
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-white opacity-10 rounded-full transform translate-y-20 translate-x-20" />

        {/* Corner decorations */}
        <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-white/30" />
        <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-white/30" />

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-6 h-6" />
            <h3 className="font-space text-2xl font-black tracking-tight">{t('stats.achievements.title')}</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="bg-white/10 rounded-xl p-4 border border-white/20">
              <p className="text-xs text-blue-200 mb-1 uppercase tracking-wider font-medium">{t('stats.achievements.unlocked')}</p>
              <p className="font-space text-4xl font-black">{unlockedCount}/{allAchievementsList.length}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 border border-white/20">
              <p className="text-xs text-blue-200 mb-1 uppercase tracking-wider font-medium">{t('stats.achievements.totalPoints')}</p>
              <p className="font-space text-4xl font-black">{totalPoints}</p>
            </div>
          </div>
        </div>

        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      </div>

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {achievementsWithStatus.map((achievement, i) => (
          <div
            key={i}
            className={`group relative rounded-2xl p-5 transition-all duration-300 overflow-hidden ${
              achievement.unlocked
                ? 'bg-black text-white border-[3px] border-black hover:shadow-2xl cursor-pointer'
                : 'bg-gray-100 text-gray-400 border-[3px] border-gray-200'
            }`}
            style={{
              animation: `slideInScale 0.5s ease-out ${i * 0.05}s both`
            }}
          >
            {/* Corner decorations */}
            {achievement.unlocked && (
              <>
                <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-white/20" />
                <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-white/20" />
              </>
            )}

            <div className="relative z-10">
              <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-3 transition-all duration-300 ${
                achievement.unlocked 
                  ? 'bg-[#3A86FF] group-hover:scale-110 group-hover:rotate-6 border-2 border-white/10' 
                  : 'bg-gray-300'
              }`}>
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <h5 className={`font-space font-black mb-1 ${achievement.unlocked ? 'text-white' : 'text-gray-500'}`}>
                {achievement.title}
              </h5>
              <p className={`text-xs ${achievement.unlocked ? 'text-white/70' : 'text-gray-400'}`}>
                {achievement.description}
              </p>

              {achievement.unlocked && (
                <div className="mt-3 flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span className="text-xs font-bold text-yellow-400">{achievement.points || 0} pts</span>
                </div>
              )}
            </div>

            {/* Shine effect on unlocked */}
            {achievement.unlocked && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            )}

            {/* Particle dot */}
            {achievement.unlocked && (
              <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#3A86FF] opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-0 group-hover:scale-100" />
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default AchievementsDetailPanel;